import type * as React from 'react';
import { del, post } from '@Domain/Api';
import subjectArchivalChangeFailed, {
	type SubjectArchivalChangeFailedAction,
} from '@Application/Subjects/Archivals/Actions/subjectArchivalChangeFailed';
import subjectArchived, {
	type SubjectArchivedAction,
} from '@Application/Subjects/Archivals/Actions/subjectArchived';
import subjectRestored, {
	type SubjectRestoredAction,
} from '@Application/Subjects/Archivals/Actions/subjectRestored';
import type ChangeSubjectArchival from '@Application/Subjects/Archivals/Commands/ChangeSubjectArchival';
import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import fetchSubject from '@Application/Subjects/fetchSubject';
import FetchSubject from '@Application/Subjects/Queries/FetchSubject';
import type { SubjectFetchedAction } from '@Application/Subjects/Actions/subjectFetched';
import type { SubjectFetchFailedAction } from '@Application/Subjects/Actions/subjectFetchFailed';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';

type Actions =
	| SubjectArchivedAction
	| SubjectRestoredAction
	| SubjectArchivalChangeFailedAction
	| SubjectFetchedAction
	| SubjectFetchFailedAction;

/** The response carries no subject, so it is refetched; a failed refetch is ignored. */
export default (command: ChangeSubjectArchival) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			const url = `${buildSubjectApiPath(command.facilityId, command.subject)}/archivals`;

			if (command.archived) {
				await post<void>(url, {});
			} else {
				await del<void>(url);
			}

			const action = command.archived
				? subjectArchived(command.subject.subjectId)
				: subjectRestored(command.subject.subjectId);
			dispatch(action);
			await fetchSubject(new FetchSubject(command.facilityId, command.subject.subjectId))(
				dispatch,
			).catch(() => undefined);

			return action;
		} catch (error) {
			const action = subjectArchivalChangeFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
